/// <reference types="vite-plugin-svgr/client" />

import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Button, Layout, Menu, MenuProps } from 'antd';
import Sider from 'antd/lib/layout/Sider';
import RoutePaths from '../../router/RoutePaths';
import { SettingOutlined, ApiOutlined, BellOutlined, CloudServerOutlined } from '@ant-design/icons';
import { ItemType } from 'antd/es/menu/hooks/useItems';

import ChevronLeftSvg from '../../assets/icons/chevron-left.svg?react';
import ProfileSvg from '../../assets/icons/profile.svg?react';
import OrganizationSvg from '../../assets/icons/organization.svg?react';
import UserManagementSvg from '../../assets/icons/user-management.svg?react';
import MenuSideBarBottom from '../../components/MenuSideBarBottom/MenuSideBarBottom';
import { sidebarWidth, headerHeight, sidebarWidthCollapsed, sideBarIconSize } from '../../constants/styles/mixins';
import { logoutUser, OrganizationAccessRoles } from '../../common/auth';
import { useOrgRoleInfo } from '../BaseIndex/useOrgRoleInfo';
import { LogoFullIcon } from '../../assets/CustomIcons/LogoFullIcon';
import { useUnresolvedNotificationsCount } from '../../serverState/organization';
import { useUserOrganizationSocket } from '../../hooks/useUserOrganizationSocket';

export default function SettingsWrapper() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const { activeOrganization, user, organizationRole } = useOrgRoleInfo();
  // This page shows the Inbox badge too, so it needs the same live feed BaseIndex has (DEV-3223).
  useUserOrganizationSocket(activeOrganization?.id as number);
  const notificationsCount = useUnresolvedNotificationsCount(activeOrganization?.id as number);

  useEffect(() => {
    const urlSelection = location.pathname.split('/');

    // We know there are settings and one more thing after it if there are 3 or more
    if (urlSelection.length > 3) {
      if (urlSelection[3] === 'organization-settings') {
        setSelectedNavigationItem({
          label: 'Organization',
          key: 'organization',
        });
      } else if (urlSelection[3] === 'my-profile') {
        setSelectedNavigationItem({
          label: 'My profile',
          key: 'personalSettings',
        });
      } else if (urlSelection[3] === 'user-management') {
        setSelectedNavigationItem({
          label: 'User Management',
          key: 'userManagement',
        });
      } else if (urlSelection[3] === 'space-ownership') {
        setSelectedNavigationItem({
          label: 'Space Ownership',
          key: 'spaceOwnership',
        });
      } else if (urlSelection[3] === 'integrations') {
        setSelectedNavigationItem({
          label: 'Integrations',
          key: 'integrations',
        });
      } else if (urlSelection[3] === 'notifications') {
        setSelectedNavigationItem({
          label: 'Slack notifications',
          key: 'notifications',
        });
      } else if (urlSelection[3] === 'mcp') {
        setSelectedNavigationItem({
          label: 'MCP',
          key: 'mcp',
        });
      }
    }
  }, [location.pathname]);

  const getSidebarConfig = useMemo(() => {
    const sidebarBaseConfig: ItemType[] = [
      {
        label: 'My Profile',
        key: 'personalSettings',
        icon: <ProfileSvg style={{ width: sideBarIconSize }} />,
      },
      // Deliberately outside the admin branch below: this page connects the signed-in
      // person's OWN accounts, which every member does for themselves.
      {
        label: 'Slack notifications',
        key: 'notifications',
        icon: <BellOutlined style={{ width: sideBarIconSize }} />,
      },
    ];

    if (organizationRole && organizationRole === OrganizationAccessRoles.ADMIN) {
      sidebarBaseConfig.push({
        label: 'Organization Settings',
        key: 'organization',
        icon: <OrganizationSvg style={{ width: sideBarIconSize }} />,
      });
      // Connecting a GitHub installation binds an external account to this whole
      // organization - admin-only, same as Organization Settings.
      sidebarBaseConfig.push({
        label: 'Integrations',
        key: 'integrations',
        icon: <ApiOutlined style={{ width: sideBarIconSize }} />,
      });
    }
    // Outside the admin branch: every member connects their own AI client. Sits after
    // Integrations so admins see the design's order. (#3076)
    sidebarBaseConfig.push({
      label: 'MCP',
      key: 'mcp',
      icon: <CloudServerOutlined style={{ width: sideBarIconSize }} />,
    });
    sidebarBaseConfig.push({
      label: 'User Management',
      key: 'userManagement',
      icon: <UserManagementSvg style={{ width: sideBarIconSize }} />,
    });
    return sidebarBaseConfig;
  }, [organizationRole]);

  const [selectedNavigationItem, setSelectedNavigationItem] = useState<ItemType | undefined>({
    key: '',
    label: '',
  });

  const sidebarItemSelected: MenuProps['onClick'] = async (e) => {
    if (e.key === 'logout') {
      await logoutUser();
      return;
    }

    let itemLabel = 'Item Selected';

    getSidebarConfig.forEach((item) => {
      if (item && Object.prototype.hasOwnProperty.call(item, 'children')) {
        // We know item has children
        // Looping over them
        if ('children' in item) {
          item.children?.forEach((child) => {
            if (child && Object.prototype.hasOwnProperty.call(child, 'label') && child.key === e.key) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore: I check if field exist above
              itemLabel = child.label as string;
              return;
            }
          });
        }
      } else {
        if (item && Object.prototype.hasOwnProperty.call(item, 'label') && item.key === e.key) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: I check if field exist above
          itemLabel = item.label as string;
        }
      }
      return;
    });

    setSelectedNavigationItem({
      key: e.key,
      label: itemLabel,
    });

    if (e.key === 'personalSettings') {
      navigate(`/${activeOrganization?.publicId}/settings/my-profile`);
    }

    if (e.key === 'organization') {
      navigate(`/${activeOrganization?.publicId}/settings/organization-settings`);
    }

    if (e.key === 'userManagement') {
      navigate(`/${activeOrganization?.publicId}/settings/user-management`);
    }

    if (e.key === 'spaceOwnership') {
      navigate(`/${activeOrganization?.publicId}/settings/space-ownership`);
    }

    if (e.key === 'integrations') {
      navigate(`/${activeOrganization?.publicId}/settings/integrations`);
    }

    if (e.key === 'notifications') {
      navigate(`/${activeOrganization?.publicId}/settings/notifications`);
    }

    if (e.key === 'mcp') {
      navigate(`/${activeOrganization?.publicId}/settings/mcp`);
    }
  };

  return (
    <Layout hasSider={true} style={{ minHeight: '100vh' }}>
      <Sider
        style={{
          borderRight: '1px solid var(--color-neutral-300)',
          paddingLeft: 16,
          paddingRight: 16,
        }}
        width={sidebarWidth}
        theme="light"
        collapsible={true}
        collapsed={collapsed}
        collapsedWidth={sidebarWidthCollapsed}
        trigger={null}
      >
        <div
          style={{ height: headerHeight }}
          className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}
        >
          {!collapsed && <LogoFullIcon onClick={() => navigate(RoutePaths.Home)} />}
          <Button
            size="small"
            shape="default"
            className="flex items-center justify-center"
            icon={
              collapsed ? (
                <ChevronLeftSvg width={16} style={{ transform: 'rotate(180deg)' }} className="pb-1" />
              ) : (
                <ChevronLeftSvg width={16} className="pt-1" />
              )
            }
            onClick={() => setCollapsed(!collapsed)}
          />
        </div>
        <div
          className="flex flex-col"
          style={{
            height: `calc(100% - ${headerHeight + 20}px)`,
          }}
        >
          <div className="flex gap-1 ml-4 mb-2 cursor-pointer" onClick={() => navigate(RoutePaths.Home)}>
            <ChevronLeftSvg width={16} className="text-neutral-900" />
            {!collapsed && <span className="text-neutral-900 text-sm font-medium">Back</span>}
          </div>

          <div className="flex gap-2 ml-4 mb-4">
            <SettingOutlined width={16} className="text-brand-grey-dark" />
            {!collapsed && <span className="text-brand-grey-dark text-base font-semibold">Settings</span>}
          </div>
          <Menu
            theme="light"
            mode="inline"
            items={getSidebarConfig}
            style={{ borderRadius: 8, backgroundColor: 'var(--color-brand-white)', border: 'none' }}
            onClick={sidebarItemSelected}
            selectedKeys={[selectedNavigationItem?.key as string]}
            inlineCollapsed={collapsed}
            className="mb-auto"
          />
          <MenuSideBarBottom
            activeOrganization={activeOrganization}
            collapsed={collapsed}
            navigate={navigate}
            notificationsCount={notificationsCount}
            user={user}
            organizationRole={organizationRole as OrganizationAccessRoles}
            inboxOpen={false}
            onToggleInbox={() => navigate(RoutePaths.Home)}
          />
        </div>
      </Sider>
      <Layout className="site-layout bg-white">
        {/* Content of the pages. Rest is navigation */}
        <Outlet />
      </Layout>
    </Layout>
  );
}
